import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LOAD_SUBS } from '../../+state/customer-list/actions';
import { PrettifyDateString } from '../../../../../../../../../angular/ui/pipes/src/lib/pipes/pretify-date-time-strings';
import { EnvironmentService } from '../../../../../../../../../angular/services/src/lib/environment/environment.service';
import { Store } from '@ngrx/store';
import { LazyLoadEvent } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomerModalComponent } from '../customer-modal/customer-modal.component';
import { Subject } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import {
    CustomerListSelectors,
    CustomerListActions,
} from '../../+state/customer-list';
import {
    CourseStatus,
    CustomerListData,
    CustomerListDataCourse,
    CustomerListDataExam,
} from '../../../../../../../shared/interfaces/src';
import { CustomerListQuery, FilterFields, BookingType } from '../../model';
import { UUID } from '../../../../../../../../../shared/generic-types/src';
import { FilterService } from '../../services/filter.service';

type VisibleCustomerListDataCourse = Omit<
    CustomerListDataCourse,
    'extensions' | 'exams'
> & {
    isVisible?: boolean;
    extensions: (CustomerListDataCourse['extensions'][number] & {
        isVisible?: boolean;
    })[];
    exams: (CustomerListDataExam & { isVisible?: boolean })[];
};

type CustomerListBooking = {
    type: BookingType;
    bookingDate: string;
    course: VisibleCustomerListDataCourse;
    extension?: VisibleCustomerListDataCourse['extensions'][number];
    exam?: VisibleCustomerListDataCourse['exams'][number];
};

@Component({
    selector: 'cna-list-customer',
    templateUrl: './list-customer.component.html',
    styleUrls: ['./list-customer.component.css'],
    encapsulation: ViewEncapsulation.None,
    providers: [DialogService],
})
export class ListCustomerComponent implements OnInit, OnDestroy {
    customers$ = this.store.select(CustomerListSelectors.GET_CURRENT_LIST).pipe(
        filter(customers => !customers.includes(undefined)),
        withLatestFrom(this.store.select(CustomerListSelectors.GET_FILTER)),
        map(([customers, currentFilter]) =>
            this.filterService.applyFiltersLocal(customers, currentFilter)
        ),
        withLatestFrom(this.store.select(CustomerListSelectors.GET_SELECTED)),
        map(([customers, selected]) => this.mapForRowSpan(customers, selected))
    );
    isNoneSelected$ = this.store.select(
        CustomerListSelectors.GET_IS_NONE_SELECTED
    );
    isAllSelected$ = this.store.select(
        CustomerListSelectors.GET_IS_ALL_SELECTED
    );
    selectedCount$ = this.store.select(
        CustomerListSelectors.GET_SELECTED_COUNT
    );
    total$ = this.store.select(CustomerListSelectors.GET_TOTAL);
    loading$ = this.store.select(CustomerListSelectors.GET_LOADING);
    query$ = this.store.select(CustomerListSelectors.GET_QUERY);

    FilterFields = FilterFields;
    BookingType = BookingType;

    private readonly _destroying$ = new Subject<void>();
    private modalRef: DynamicDialogRef;

    constructor(
        private router: Router,
        private prettifyDateString: PrettifyDateString,
        private environmentService: EnvironmentService,
        private store: Store,
        private dialogService: DialogService,
        public filterService: FilterService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.filterService.resetAll();
        this.store.dispatch(CustomerListActions.LOAD_FILTER_VALUES());
        this.store.dispatch(LOAD_SUBS());
    }

    async ngOnDestroy(): Promise<void> {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    async openModal(customer: CustomerListData, activeTab = 0): Promise<void> {
        this.modalRef = this.dialogService.open(CustomerModalComponent, {
            data: {
                id: customer.id,
                sub: customer.sub,
                activeTab,
            },
            header: 'Nutzerdetails: ' + customer.displayName,
            width: '80%',
            height: '90%',
            contentStyle: {
                backgroundColor: '#fff',
                height: '100%',
            },
            dismissableMask: true,
        });
    }

    mailtoSelected() {
        this.store.dispatch(CustomerListActions.MAIL_TO_SELECTED());
    }

    createExport(): void {
        this.store.dispatch(CustomerListActions.DOWNLOAD_USER_EXPORT());
    }

    async load(
        event: LazyLoadEvent,
        currentQuery: CustomerListQuery
    ): Promise<void> {
        if (
            event.first !== currentQuery.first ||
            event.rows !== currentQuery.rows
        ) {
            this.store.dispatch(
                CustomerListActions.SET_QUERY({
                    query: {
                        first: event.first,
                        rows: event.rows,
                    },
                })
            );
        }
    }

    async toggle(sub: UUID): Promise<void> {
        return this.store.dispatch(CustomerListActions.TOGGLE_SELECT({ sub }));
    }

    async selectAll(): Promise<void> {
        return this.store.dispatch(CustomerListActions.SELECT_ALL());
    }

    async deselectAll(): Promise<void> {
        return this.store.dispatch(CustomerListActions.DESELECT_ALL());
    }

    private mapForRowSpan(
        customers: CustomerListData[],
        selected: UUID[]
    ): (CustomerListData & {
        courses: VisibleCustomerListDataCourse[];
        bookings: CustomerListBooking[];
        selected: boolean;
    })[] {
        return customers.map(customer => {
            const courses = customer.courses as VisibleCustomerListDataCourse[];

            return {
                ...customer,
                courses,
                bookings: this.mapBookings(courses),
                selected: selected.includes(customer.sub),
            };
        });
    }

    private mapBookings(
        courses: VisibleCustomerListDataCourse[]
    ): CustomerListBooking[] {
        const bookings = courses.reduce<CustomerListBooking[]>(
            (result, course) => {
                if (course.isVisible) {
                    result.push({
                        type: BookingType.COURSE,
                        bookingDate: course.bookingDate,
                        course,
                    });
                }

                (course.extensions ?? [])
                    .filter(extension => extension.isVisible)
                    .forEach(extension =>
                        result.push({
                            type: BookingType.EXTENSION,
                            bookingDate: extension.bookingDate,
                            course,
                            extension,
                        })
                    );

                (course.exams ?? [])
                    .filter(exam => exam.isVisible)
                    .forEach(exam =>
                        result.push({
                            type: BookingType.EXAM,
                            bookingDate: exam.bookingDate,
                            course,
                            exam,
                        })
                    );

                return result;
            },
            []
        );

        return bookings
            .map((booking, index) => ({ booking, index }))
            .sort(
                (booking1, booking2) =>
                    new Date(booking2.booking.bookingDate).getTime() -
                        new Date(booking1.booking.bookingDate).getTime() ||
                    booking1.index - booking2.index
            )
            .map(({ booking }) => booking);
    }

    protected readonly CourseStatus = CourseStatus;
}
